import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Building2, Send, Clock, ShieldCheck, DatabaseZap } from "lucide-react";

export default function Admin() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [restoring, setRestoring] = useState(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([http.get("/admin/stats"), http.get("/admin/pending-companies")]);
      setStats(s.data); setPending(p.data);
    } catch (e) {
      toast.error("Admin only"); nav("/");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.user_type !== "admin") { toast.error("Admin only"); nav("/"); return; }
    load();
  }, [user, nav, loading]);

  const verify = async (id) => {
    await http.put(`/admin/verify-company/${id}`);
    toast.success(t(lang, "verify"));
    load();
  };

  const rehydrateCompanies = async () => {
    const msg = lang === "fr"
      ? "Recharger les 8 500+ entreprises depuis le bundle local ? (upsert par nom+ville, aucun doublon, aucun crédit Google Places consommé)"
      : "Reload 8 500+ companies from the local bundle? (upsert by name+city, no duplicates, no Google Places credit consumed)";
    if (!window.confirm(msg)) return;
    setRestoring(true);
    try {
      const { data } = await http.post("/admin/companies/restore-from-seed");
      toast.success(
        lang === "fr"
          ? `Restauration OK — ${data.inserted} ajoutées, ${data.updated} mises à jour, ${data.total} au total.`
          : `Restore OK — ${data.inserted} inserted, ${data.updated} updated, ${data.total} total.`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "admin_title")}</h1>
          <Button
            data-testid="rehydrate-companies-btn"
            onClick={rehydrateCompanies}
            disabled={restoring}
            className="bg-[#F97316] hover:bg-[#EA580C] rounded-full h-10 px-5"
            title={lang === "fr" ? "Recharge la base companies depuis le bundle local (idempotent)" : "Reload companies from local bundle (idempotent)"}
          >
            <DatabaseZap className="w-4 h-4 mr-1.5" />
            {restoring
              ? (lang === "fr" ? "Restauration…" : "Restoring…")
              : (lang === "fr" ? "Recharger les entreprises" : "Rehydrate companies")}
          </Button>
        </div>

        {stats && (
          <div className="mt-8 grid md:grid-cols-4 gap-4">
            {[
              { icon: Users, label: "Users", val: stats.users },
              { icon: Building2, label: "Companies", val: stats.companies_total },
              { icon: Send, label: "Applications", val: stats.applications },
              { icon: Clock, label: "Pending", val: stats.companies_pending },
            ].map((s) => (
              <div key={`stat-${s.label}`} className="rounded-2xl bg-white border border-slate-200 p-5">
                <s.icon className="w-5 h-5 text-[#1E3A8A]" />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-3">{s.label}</p>
                <p className="font-display text-3xl font-bold text-[#1E3A8A]">{s.val}</p>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-10 mb-4">{t(lang, "pending_moderation")}</p>
        <div className="space-y-3">
          {pending.map((c) => (
            <div key={c.id} data-testid={`pending-${c.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="font-semibold">{c.name}</p>
                <p className="text-xs text-slate-500">{c.sector} · {c.city}, {c.country}</p>
                <p className="text-sm text-slate-600 mt-1 line-clamp-1">{c.description}</p>
              </div>
              <Button data-testid={`verify-${c.id}`} onClick={() => verify(c.id)} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full">
                <ShieldCheck className="w-4 h-4 mr-1.5" /> {t(lang, "verify")}
              </Button>
            </div>
          ))}
          {pending.length === 0 && <p className="text-sm text-slate-500 text-center py-6">—</p>}
        </div>
      </div>
    </Layout>
  );
}
